import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  Linkedin,
  Mail,
  ExternalLink,
  Calendar,
  BarChart2,
  BookOpen,
  User,
} from 'lucide-react';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';

interface ActivityLog {
  id: string;
  user_id: string;
  user_email: string | null;
  auth_provider: string | null;
  event_type: string;
  parcours_id: string | null;
  parcours_slug: string | null;
  parcours_company_name: string | null;
  step_id: string | null;
  step_title: string | null;
  page_path: string | null;
  user_agent: string | null;
  created_at: string;
}

function fmtDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

function fmtDateShort(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

function EventTypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; color: string }> = {
    view_parcours_step:   { label: 'Vue étape',       color: 'bg-blue-50 text-blue-700' },
    start_parcours:       { label: 'Début parcours',  color: 'bg-emerald-50 text-emerald-700' },
    complete_step:        { label: 'Étape complète',  color: 'bg-amber-50 text-amber-700' },
    complete_parcours:    { label: 'Parcours complet', color: 'bg-[#d03484]/10 text-[#d03484]' },
  };
  const { label, color } = map[type] ?? { label: type, color: 'bg-neutral-100 text-neutral-600' };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${color}`}>
      {label}
    </span>
  );
}

function ProviderBadge({ provider }: { provider: string | null }) {
  if (provider === 'linkedin_oidc') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0077b5]/10 px-2.5 py-0.5 text-xs font-semibold text-[#0077b5]">
        <Linkedin className="w-3 h-3" />
        LinkedIn
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-600">
      <Mail className="w-3 h-3" />
      {provider === 'email' ? 'Email / Mot de passe' : (provider ?? 'Inconnu')}
    </span>
  );
}

export default function CandidatDetailAdmin() {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Guard: admin/editor only
  useEffect(() => {
    if (!user) { navigate('/'); return; }
    supabase.from('users').select('role').eq('id', user.id).maybeSingle()
      .then(({ data }) => {
        if (!data || !['admin', 'editor'].includes(data.role)) {
          navigate('/');
        } else {
          setUserRole(data.role);
        }
      });
  }, [user?.id]);

  useEffect(() => {
    if (!userRole || !userId) return;
    supabase
      .from('candidate_activity_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        if (!error && data) setLogs(data as ActivityLog[]);
        setLoading(false);
      });
  }, [userRole, userId]);

  if (!userRole) return null;

  const email = logs[0]?.user_email ?? userId ?? '—';
  const provider = logs[0]?.auth_provider ?? null;
  const firstActivity = logs.length > 0 ? logs[logs.length - 1].created_at : null;
  const lastActivity = logs.length > 0 ? logs[0].created_at : null;
  const distinctParcours = new Set(logs.map(l => l.parcours_id).filter(Boolean));

  return (
    <>
      <Helmet>
        <title>Activité candidat | Admin recruteurs.io</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-[#f7f7f8]">
        {/* Header */}
        <div className="bg-white border-b border-neutral-100 px-6 py-4 flex items-center gap-3">
          <Link
            to="/admin/candidats"
            className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Journal candidats
          </Link>
          <span className="text-neutral-300">/</span>
          <span className="text-sm font-semibold text-neutral-800 truncate max-w-xs">{email}</span>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Profile card */}
          <div className="bg-white rounded-xl border border-neutral-100 p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center flex-shrink-0">
              <User className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-neutral-900 truncate">{email}</p>
              <div className="flex flex-wrap items-center gap-3 mt-1.5">
                <ProviderBadge provider={provider} />
                <span className="text-xs text-neutral-400 font-mono truncate max-w-xs">{userId}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Événements', value: logs.length, icon: <BarChart2 className="w-5 h-5 text-[#d03484]" /> },
              { label: 'Parcours consultés', value: distinctParcours.size, icon: <BookOpen className="w-5 h-5 text-[#d47254]" /> },
              { label: 'Première activité', value: firstActivity ? fmtDateShort(firstActivity) : '—', icon: <Calendar className="w-5 h-5 text-neutral-400" /> },
              { label: 'Dernière activité', value: lastActivity ? fmtDateShort(lastActivity) : '—', icon: <Calendar className="w-5 h-5 text-emerald-500" /> },
            ].map(({ label, value, icon }) => (
              <div key={label} className="bg-white rounded-xl border border-neutral-100 p-4 flex items-start gap-3">
                <div className="mt-0.5">{icon}</div>
                <div className="min-w-0">
                  <p className="text-base font-bold text-neutral-900 leading-tight">{value}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">{label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Activity timeline */}
          <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-neutral-100">
              <h2 className="text-sm font-semibold text-neutral-800">Historique d'activité</h2>
            </div>

            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-10 bg-neutral-50 rounded animate-pulse" />
                ))}
              </div>
            ) : logs.length === 0 ? (
              <div className="py-16 text-center">
                <BarChart2 className="w-8 h-8 text-neutral-200 mx-auto mb-3" />
                <p className="text-neutral-500 text-sm">Aucune activité enregistrée.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-neutral-100">
                      <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">Date / heure</th>
                      <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Événement</th>
                      <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Entreprise</th>
                      <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Étape</th>
                      <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Page</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-50">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-neutral-50 transition-colors">
                        <td className="px-5 py-3 text-neutral-500 whitespace-nowrap text-xs">
                          {fmtDate(log.created_at)}
                        </td>
                        <td className="px-5 py-3">
                          <EventTypeBadge type={log.event_type} />
                        </td>
                        <td className="px-5 py-3 text-neutral-700 max-w-[180px] truncate">
                          {log.parcours_company_name ? (
                            <Link
                              to={`/decouvrir/${log.parcours_slug}`}
                              className="hover:text-[#d03484] transition-colors"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {log.parcours_company_name}
                            </Link>
                          ) : '—'}
                        </td>
                        <td className="px-5 py-3 text-neutral-600 max-w-[200px] truncate">
                          {log.step_title ?? '—'}
                        </td>
                        <td className="px-5 py-3">
                          {log.page_path ? (
                            <a
                              href={log.page_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-[#d03484] transition-colors font-mono max-w-[180px] truncate"
                            >
                              {log.page_path}
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
